'use client';

import React, {
  forwardRef,
  useCallback,
  useImperativeHandle,
  useMemo,
} from 'react';
import { useTranslations } from 'next-intl';
import { ButtonV2 } from 'components/shared/ButtonV2';
import { ImageCardModal } from 'components/shared/ImageCardModal';
import { CustomImage } from 'components/shared/custom-image';
import { useLocationMatch } from 'hooks/navigation/useLocationMatch';
import { useIdlePopup } from 'hooks/popup/useIdlePopup';
import type { IdlePopupHandle } from 'hooks/popup/constants';
import {
  BANGALORE_EXPERIENCE_CENTRE,
  BANGALORE_LOCATION_TARGET,
  BANGALORE_TRY_ON_ENABLED,
  BANGALORE_TRY_ON_STORAGE_KEYS,
  BANGALORE_TRY_ON_TRACKING,
  BANGALORE_UBER_DEEPLINK,
  type BangaloreTryOnCardProps,
  type BangaloreTryOnPopupProps,
} from './constants';
import styles from './index.module.css';

const CtaChevron = () => (
  <svg
    className={styles.chevronArrow}
    width="12"
    height="12"
    viewBox="0 0 12 12"
    fill="none"
    aria-hidden="true"
  >
    <path
      d="M4.5 2.5L8 6L4.5 9.5"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const BangaloreTryOnCard = forwardRef<IdlePopupHandle, BangaloreTryOnCardProps>(
  ({ delayMs, region, matchMethod }, ref) => {
    const t = useTranslations('common');
    const trackingData = useMemo(
      () => ({ region, matchMethod }),
      [region, matchMethod],
    );
    const { isVisible, cancel, dismiss, complete } = useIdlePopup({
      storageKeys: BANGALORE_TRY_ON_STORAGE_KEYS,
      tracking: BANGALORE_TRY_ON_TRACKING,
      delayMs,
      trackingData,
    });

    useImperativeHandle(ref, () => ({ cancelPopup: cancel }), [cancel]);

    const openExternal = useCallback(
      (url: string) => {
        complete();
        window.open(url, '_blank', 'noopener,noreferrer');
      },
      [complete],
    );

    if (!isVisible) return null;

    return (
      <ImageCardModal
        onClose={dismiss}
        imageFit="fill"
        image={
          <CustomImage
            src={BANGALORE_EXPERIENCE_CENTRE.imageSrc}
            alt={t(
              'claimSizingKitPopup.bangaloreUber.kitImage.alt.experienceCentre',
            )}
            width={744}
            height={420}
          />
        }
        title={t('claimSizingKitPopup.bangaloreUber.title.comeTryItOn')}
        subtitle={t('claimSizingKitPopup.bangaloreUber.subtitle.bestWayToPick')}
        actions={
          <>
            <ButtonV2
              variant="blue"
              onClick={() => openExternal(BANGALORE_EXPERIENCE_CENTRE.mapsUrl)}
            >
              {t('claimSizingKitPopup.bangaloreUber.cTALink.getDirections')}
            </ButtonV2>
            <button
              type="button"
              className={`${styles.ctaLink} ${styles.ctaLinkMobileOnly}`}
              onClick={() => openExternal(BANGALORE_UBER_DEEPLINK)}
            >
              {t('claimSizingKitPopup.bangaloreUber.cTALink.bookMyRide')}
              <CtaChevron />
            </button>
          </>
        }
      />
    );
  },
);
BangaloreTryOnCard.displayName = 'BangaloreTryOnCard';

const BangaloreLocationGate = forwardRef<
  IdlePopupHandle,
  BangaloreTryOnPopupProps
>(({ fallback, ...cardProps }, ref) => {
  const { status, matched, method } = useLocationMatch(
    BANGALORE_LOCATION_TARGET,
  );

  if (status === 'pending') return null;
  if (!matched) return <>{fallback}</>;
  return <BangaloreTryOnCard ref={ref} {...cardProps} matchMethod={method} />;
});
BangaloreLocationGate.displayName = 'BangaloreLocationGate';

/**
 * Shows the Bengaluru Experience Centre popup to local visitors, `fallback` to
 * everyone else. Pass the same ref as the fallback: only one is ever mounted.
 */
export const BangaloreTryOnPopup = forwardRef<
  IdlePopupHandle,
  BangaloreTryOnPopupProps
>((props, ref) =>
  BANGALORE_TRY_ON_ENABLED ? (
    <BangaloreLocationGate ref={ref} {...props} />
  ) : (
    <>{props.fallback}</>
  ),
);
BangaloreTryOnPopup.displayName = 'BangaloreTryOnPopup';
